import TenantPaymentTimeline from "./TenantPaymentTimeline";
import ActionMenu from "./ActionMenu";
import React, { useEffect, useMemo, useState } from "react";
import api from "../api";
import bgVideo from "../assets/bg.mp4";
import { printReceipt } from "../utils/receipt";

function Toast({ show, message, type, onClose }) {
  if (!show) return null;

  return (
    <div
      className={`fixed right-4 top-4 z-50 rounded-lg px-5 py-3 text-sm font-semibold text-white shadow-2xl ${
        type === "success" ? "bg-teal-600" : "bg-red-600"
      }`}
    >
      <div className="flex items-center gap-4">
        <span>{message}</span>
        <button onClick={onClose} className="text-white/80 hover:text-white">
          X
        </button>
      </div>
    </div>
  );
}

function ProofModal({ proof, title, onClose }) {
  if (!proof) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="relative flex max-h-[88vh] w-full max-w-5xl flex-col rounded-lg border border-white/10 bg-slate-950 p-4 shadow-2xl">
        <div className="mb-3 flex items-center justify-between border-b border-white/10 pb-3">
          <h3 className="text-lg font-bold text-white">{title || "Payment Proof"}</h3>
          <button
            onClick={onClose}
            className="rounded-lg border border-white/15 px-3 py-1 text-sm font-semibold text-slate-200 transition hover:bg-white/10"
          >
            X
          </button>
        </div>
        <img
          src={`data:image/*;base64,${proof}`}
          alt="Payment proof"
          className="min-h-0 w-full flex-1 rounded object-contain"
        />
      </div>
    </div>
  );
}

function ReviewModal({ payment, action, reason, setReason, onClose, onConfirm }) {
  if (!payment) return null;

  const isClarify = action === "clarify";
  const tone = isClarify ? "text-amber-300" : "text-red-300";
  const buttonClass = isClarify
    ? "rounded-lg bg-amber-500 px-5 py-2 font-semibold text-slate-950 transition hover:bg-amber-400"
    : "rounded-lg bg-red-600 px-5 py-2 font-semibold text-white transition hover:bg-red-500";
  const label = isClarify ? "Needs Clarification" : "Reject Payment";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-lg rounded-lg border border-white/10 bg-slate-950 p-6 text-white shadow-2xl">
        <div className="mb-5 border-b border-white/10 pb-4">
          <p className={`text-xs font-semibold uppercase tracking-[0.25em] ${tone}`}>
            {label}
          </p>
          <h3 className="mt-2 text-2xl font-bold">
            {payment.tenant_name || "Unknown Tenant"}
          </h3>
          <p className="mt-1 text-sm text-slate-400">
            Add a clear note so the tenant knows what to correct.
          </p>
        </div>

        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={4}
          className="w-full resize-none rounded-lg border border-white/10 bg-white px-3 py-3 text-sm text-slate-900 outline-none transition focus:border-red-400 focus:ring-2 focus:ring-red-400/30"
          placeholder={isClarify ? "What needs clarification?" : "Reason for rejection"}
        />

        <div className="mt-5 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="rounded-lg border border-white/15 px-5 py-2 font-semibold text-slate-200 transition hover:bg-white/10"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={buttonClass}
          >
            {label}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminPaymentList() {
  const [view, setView] = useState("review");
  const [loadError, setLoadError] = useState("");
  const [payments, setPayments] = useState([]);
  const [balances, setBalances] = useState([]);
  const [ledger, setLedger] = useState({ month_paid_for: "", summary: {}, rows: [] });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [proofModal, setProofModal] = useState({ data: null, title: "Payment Proof" });
  const [reviewPayment, setReviewPayment] = useState(null);
  const [reviewAction, setReviewAction] = useState("reject");
  const [reviewReason, setReviewReason] = useState("");
  const [undoReviewPaymentId, setUndoReviewPaymentId] = useState(null);
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    const token = localStorage.getItem("token");
    if (!token) { setLoadError("Your session has expired. Sign in again to view payments."); setLoading(false); return; }
    setLoading(true);
    setLoadError("");

    try {
      const [paymentsRes, balancesRes, ledgerRes] = await Promise.all([
        api.get("/admin/payments", {
          headers: { Authorization: `Bearer ${token}` },
        }),
        api.get("/admin/payment-balances", {
          headers: { Authorization: `Bearer ${token}` },
        }),
        api.get("/admin/operations/payment-ledger", {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setPayments(Array.isArray(paymentsRes.data) ? paymentsRes.data : []);
      setBalances(Array.isArray(balancesRes.data) ? balancesRes.data : []);
      setLedger(
        ledgerRes.data || { month_paid_for: "", summary: {}, rows: [] }
      );
    } catch (err) {
      console.error("Failed to fetch payments:", err);
      setLoadError("Payment records could not be loaded. Please try again.");
      setPayments([]);
      setLedger({ month_paid_for: "", summary: {}, rows: [] });
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type });
    }, 3000);
  };

  const handleStatusChange = async (paymentId, action, reason = null) => {
    const token = localStorage.getItem("token");
    const payload = { action };

    if (action === "reject") {
      payload.rejected_reason = reason;
    }

    if (action === "clarify") {
      payload.clarification_reason = reason;
    }

    try {
      await api.put(
        `/admin/payments/${paymentId}/status`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      fetchPayments();
      const actionLabel =
        action === "approve"
          ? "approved"
          : action === "clarify"
          ? "marked as needs clarification"
          : action === "reopen"
          ? "reopened to pending"
          : "rejected";

      if (["reject", "clarify"].includes(action)) {
        setUndoReviewPaymentId(paymentId);
        setTimeout(() => setUndoReviewPaymentId(null), 8000);
      } else if (action === "reopen") {
        setUndoReviewPaymentId(null);
      }

      showToast(
        `Payment ${actionLabel} successfully`,
        action === "reject" ? "error" : "success"
      );
    } catch (err) {
      console.error(err);
      showToast(err.response?.data?.message || "Failed to update payment status", "error");
    }
  };

  const openReviewModal = (payment, action) => {
    setReviewPayment(payment);
    setReviewAction(action);
    setReviewReason("");
  };

  const confirmReview = () => {
    if (!reviewPayment) return;
    handleStatusChange(reviewPayment.id, reviewAction, reviewReason);
    setReviewPayment(null);
    setReviewReason("");
  };

  const formatAmount = (amount) =>
    `PHP ${Number(amount || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const statusClass = (status) => {
    if (status === "approved") return "border-teal-300/40 bg-teal-500/15 text-teal-200";
    if (status === "rejected") return "border-red-300/40 bg-red-500/15 text-red-200";
    if (status === "needs_clarification") return "border-amber-300/40 bg-amber-500/15 text-amber-100";
    return "border-amber-300/40 bg-amber-500/15 text-amber-200";
  };

  const statusLabel = (status) =>
    status === "needs_clarification"
      ? "Needs Clarification"
      : status;

  const paymentAging = (payment) => {
    if (!payment?.created_at) return { label: "-", cls: "border-white/20 bg-white/5 text-slate-300" };
    const hours = Math.floor((Date.now() - new Date(payment.created_at).getTime()) / 3600000);
    if (hours < 24) {
      return { label: "<24h", cls: "border-teal-300/40 bg-teal-500/15 text-teal-200" };
    }
    if (hours <= 72) {
      return { label: "24-72h", cls: "border-amber-300/40 bg-amber-500/15 text-amber-200" };
    }
    return { label: ">72h", cls: "border-red-300/40 bg-red-500/15 text-red-200" };
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleString();
  };

  const receiptForPayment = async (paymentId) => {
    try {
      const res = await api.get(`/payments/${paymentId}/receipt`);
      printReceipt(res.data);
    } catch (err) {
      console.error(err);
      showToast(err.response?.data?.message || "Failed to generate receipt", "error");
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const response = await api.get("/admin/operations/export/payments", {
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `payments-export-${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      showToast("Failed to export payments CSV", "error");
    } finally {
      setExporting(false);
    }
  };

  const filteredPayments = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    return payments.filter((payment) => {
      const matchesStatus =
        statusFilter === "all" || payment.status === statusFilter;
      const matchesSearch =
        !query ||
        [
          payment.tenant_name,
          payment.amount,
          payment.expected_amount,
          payment.balance_after,
          payment.payment_type,
          payment.reference_number,
          payment.property?.property_name,
          payment.property?.location,
          payment.method,
          payment.month_paid_for,
          payment.status,
          payment.rejected_reason,
          payment.clarification_reason,
          payment.appeal_message,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query));

      return matchesStatus && matchesSearch;
    });
  }, [payments, searchTerm, statusFilter]);

  const ledgerSummary = ledger.summary || {};

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 text-white">
        Loading payments...
      </div>
    );
  }

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-slate-950 font-sans text-white">
      <div className="absolute inset-0">
        <video data-workspace-background="true" aria-hidden="true" autoPlay loop muted playsInline className="h-full w-full object-cover">
          <source src={bgVideo} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-slate-950/85" />
      </div>

      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() =>
          setToast({ show: false, message: "", type: toast.type })
        }
      />
      <ProofModal
        proof={proofModal.data}
        title={proofModal.title}
        onClose={() => setProofModal({ data: null, title: "Payment Proof" })}
      />
      <ReviewModal
        payment={reviewPayment}
        action={reviewAction}
        reason={reviewReason}
        setReason={setReviewReason}
        onClose={() => {
          setReviewPayment(null);
          setReviewReason("");
        }}
        onConfirm={confirmReview}
      />

      <div className="workspace-page-body relative z-10 min-h-screen px-4 py-6 sm:px-6 lg:px-8">
        <div className="payment-page-heading mb-6 flex flex-col gap-4 border-b border-white/10 pb-5">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.35em] text-teal-300">
              Admin Payments
            </p>
            <h1 className="mt-2 text-3xl font-bold tracking-wide md:text-4xl">
              Payment Management
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-300 md:text-base">
              Review submitted payments, inspect proof, approve, reject, or ask
              tenants for clarification.
            </p>
          </div>

        </div>

        <nav className="payment-view-switch" aria-label="Payment views">
          <button type="button" aria-pressed={view === "review"} onClick={() => setView("review")}>Payment review</button>
          <button type="button" aria-pressed={view === "history"} onClick={() => setView("history")}>Tenant history</button>
        </nav>
        {loadError ? <div role="alert" className="ws-error">{loadError} <button type="button" className="ws-text-link" onClick={fetchPayments}>Try again</button></div> : view === "history" ?
          <TenantPaymentTimeline payments={payments} onReceipt={receiptForPayment} /> : <>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-white/10 bg-slate-950/90 p-3">
          <p className="text-sm text-slate-300">
            Clarification flow: when admin marks <span className="font-semibold text-amber-200">Needs Clarification</span>,
            tenant responds with note/proof, then it returns to <span className="font-semibold text-teal-200">Pending</span> for review.
          </p>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-slate-100 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {exporting ? "Exporting..." : "Export Payments CSV"}
          </button>
          {undoReviewPaymentId && (
            <button
              onClick={() => handleStatusChange(undoReviewPaymentId, "reopen")}
              className="rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-slate-100 transition hover:bg-white/10"
            >
              Undo Last Review
            </button>
          )}
        </div>

        {balances.length > 0 && (
          <div className="mb-5 grid gap-3 xl:grid-cols-3">
            {balances.slice(0, 6).map((item) => (
              <div
                key={item.property_id}
                className="rounded-lg border border-white/10 bg-slate-950/88 p-4 shadow-xl"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-teal-300">
                      Tenant Balance
                    </p>
                    <h3 className="mt-2 font-bold text-white">
                      {item.tenant_name || "Unassigned Tenant"}
                    </h3>
                    <p className="mt-1 text-sm text-slate-400">
                      {item.property_name}
                    </p>
                  </div>
                  <div className="rounded-lg border border-teal-300/30 bg-teal-500/10 px-3 py-2 text-right">
                    <p className="text-xs uppercase tracking-[0.16em] text-slate-400">
                      Balance
                    </p>
                    <p className="mt-1 font-bold text-teal-200">
                      {formatAmount(item.remaining_balance)}
                    </p>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
                  <div className="rounded border border-white/10 bg-white/5 p-2">
                    <p className="text-slate-500">Rent</p>
                    <p className="mt-1 font-semibold text-white">
                      {formatAmount(item.expected_amount)}
                    </p>
                  </div>
                  <div className="rounded border border-white/10 bg-white/5 p-2">
                    <p className="text-slate-500">Paid</p>
                    <p className="mt-1 font-semibold text-white">
                      {formatAmount(item.approved_paid)}
                    </p>
                  </div>
                  <div className="rounded border border-white/10 bg-white/5 p-2">
                    <p className="text-slate-500">Pending</p>
                    <p className="mt-1 font-semibold text-amber-200">
                      {formatAmount(item.pending_paid)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {ledger.rows?.length > 0 && (
          <div className="mb-5 rounded-lg border border-white/10 bg-slate-950/90 p-4 shadow-2xl">
            <div className="mb-4 flex flex-wrap items-end justify-between gap-3 border-b border-white/10 pb-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">
                  Property Ledger
                </p>
                <h3 className="mt-1 text-xl font-bold text-white">
                  {ledger.month_paid_for || "Current Month"}
                </h3>
              </div>
              <div className="grid grid-cols-2 gap-2 text-center sm:grid-cols-4">
                {[
                  ["Expected", formatAmount(ledgerSummary.expected_amount), "text-white"],
                  ["Approved", formatAmount(ledgerSummary.approved_amount), "text-teal-200"],
                  ["Pending", formatAmount(ledgerSummary.pending_amount), "text-amber-200"],
                  ["Remaining", formatAmount(ledgerSummary.remaining_amount), "text-amber-200"],
                ].map(([label, value, color]) => (
                  <div key={label} className="rounded-lg border border-white/10 bg-white/5 px-3 py-2">
                    <p className={`text-sm font-bold ${color}`}>{value}</p>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                      {label}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {ledger.rows.slice(0, 9).map((row) => (
                <div
                  key={row.property_id}
                  className="rounded-lg border border-white/10 bg-white/5 p-3"
                >
                  <p className="font-semibold text-white">{row.property_name}</p>
                  <p className="mt-1 text-xs text-slate-400">
                    {row.tenant_name || "Unassigned tenant"} - {row.location || "No location"}
                  </p>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                    <div className="rounded border border-white/10 bg-slate-950/60 p-2">
                      <p className="text-slate-500">Expected</p>
                      <p className="mt-1 font-semibold text-white">
                        {formatAmount(row.expected_amount)}
                      </p>
                    </div>
                    <div className="rounded border border-white/10 bg-slate-950/60 p-2">
                      <p className="text-slate-500">Approved</p>
                      <p className="mt-1 font-semibold text-teal-200">
                        {formatAmount(row.approved_amount)}
                      </p>
                    </div>
                    <div className="rounded border border-white/10 bg-slate-950/60 p-2">
                      <p className="text-slate-500">Pending</p>
                      <p className="mt-1 font-semibold text-amber-200">
                        {formatAmount(row.pending_amount)}
                      </p>
                    </div>
                    <div className="rounded border border-white/10 bg-slate-950/60 p-2">
                      <p className="text-slate-500">Remaining</p>
                      <p className="mt-1 font-semibold text-amber-200">
                        {formatAmount(row.remaining_amount)}
                      </p>
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-slate-400">
                    Collection rate:{" "}
                    <span className="font-semibold text-white">
                      {Number(row.collection_rate || 0).toFixed(1)}%
                    </span>
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mb-5 flex flex-col gap-3 rounded-lg border border-white/10 bg-slate-950/90 p-4 shadow-xl lg:flex-row lg:items-center lg:justify-between">
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-lg border border-white/10 bg-white px-3 py-3 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30 lg:max-w-md"
            placeholder="Search tenant, property, amount, reference, method, month, or status"
          />

          <label className="text-sm">Payment status
            <select className="ml-3 rounded-lg border px-3 py-2" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              {["all", "pending", "needs_clarification", "approved", "rejected"].map((status) => <option key={status} value={status}>{statusLabel(status)}</option>)}
            </select>
          </label>
        </div>

        <div className="payment-review-table hidden rounded-lg border border-white/10 bg-slate-950/90 shadow-2xl md:block">
          <div className="overflow-x-auto">
          <table className="min-w-[1500px] w-full text-left">
            <thead>
              <tr className="border-b border-white/10 bg-white/5 text-xs uppercase tracking-[0.18em] text-slate-400">
                <th className="px-5 py-4">Tenant</th>
                <th className="px-5 py-4">Property</th>
                <th className="px-5 py-4">Amount</th>
                <th className="px-5 py-4">Balance</th>
                <th className="px-5 py-4">Method</th>
                <th className="px-5 py-4">Month</th>
                <th className="px-5 py-4">Timeline</th>
                <th className="px-5 py-4">Aging</th>
                <th className="px-5 py-4">Status</th>
                <th className="px-5 py-4">Proof</th>
                <th className="px-5 py-4 text-right">Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredPayments.length > 0 ? (
                filteredPayments.map((p) => (
                  <tr key={p.id} className="border-b border-white/10 transition last:border-b-0 hover:bg-white/5">
                    <td className="px-5 py-4 font-semibold">
                      {p.tenant_name || "Unknown Tenant"}
                    </td>
                    <td className="px-5 py-4">
                      <p className="font-semibold text-white">
                        {p.property?.property_name || "Linked rental"}
                      </p>
                      <p className="text-xs capitalize text-slate-500">
                        {p.payment_type || "partial"}
                      </p>
                    </td>
                    <td className="px-5 py-4 font-semibold text-teal-200">
                      {formatAmount(p.amount)}
                    </td>
                    <td className="px-5 py-4 text-sm">
                      <p className="font-semibold text-white">
                        {formatAmount(p.balance_after)}
                      </p>
                      <p className="text-xs text-slate-500">
                        of {formatAmount(p.expected_amount)}
                      </p>
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-300">
                      <p>{p.method}</p>
                      {p.reference_number && (
                        <p className="mt-1 text-xs text-slate-500">
                          Ref: {p.reference_number}
                        </p>
                      )}
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-300">
                      {p.month_paid_for || "-"}
                    </td>
                    <td className="px-5 py-4 text-xs text-slate-400">
                      <p>Submitted: {formatDateTime(p.created_at)}</p>
                      {p.approved_at && <p>Approved: {formatDateTime(p.approved_at)}</p>}
                      {p.rejected_at && <p>Rejected: {formatDateTime(p.rejected_at)}</p>}
                      {p.clarification_at && <p>Clarified: {formatDateTime(p.clarification_at)}</p>}
                      {p.appealed_at && <p>Appealed: {formatDateTime(p.appealed_at)}</p>}
                    </td>
                    <td className="px-5 py-4">
                      <span
                        className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${
                          paymentAging(p).cls
                        }`}
                      >
                        {paymentAging(p).label}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <span
                        className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold capitalize ${statusClass(
                          p.status
                        )}`}
                      >
                        {statusLabel(p.status)}
                      </span>
                      {(p.rejected_reason || p.clarification_reason || p.appeal_message) && (
                        <p className="mt-2 max-w-xs text-xs text-slate-400">
                          {p.appeal_message || p.clarification_reason || p.rejected_reason}
                        </p>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex flex-wrap gap-2">
                        {p.proof_path ? (
                          <button
                            onClick={() =>
                              setProofModal({ data: p.proof_path, title: "Payment Proof" })
                            }
                            className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm font-semibold text-slate-100 transition hover:border-teal-300/50 hover:bg-teal-500/10"
                          >
                            View Proof
                          </button>
                        ) : (
                          <span className="text-sm text-slate-500">No proof</span>
                        )}
                        {p.proof_path && (
                          <span
                            className={`inline-flex rounded-full border px-2 py-1 text-xs font-semibold ${
                              p.proof_integrity?.status === "verified"
                                ? "border-teal-300/40 bg-teal-500/15 text-teal-200"
                                : p.proof_integrity?.status === "tampered"
                                ? "border-red-300/40 bg-red-500/15 text-red-200"
                                : "border-amber-300/40 bg-amber-500/15 text-amber-200"
                            }`}
                          >
                            Proof {p.proof_integrity?.status || "unknown"}
                          </span>
                        )}
                        {p.appeal_proof_path && (
                          <>
                            <button
                              onClick={() =>
                                setProofModal({ data: p.appeal_proof_path, title: "Appeal Proof" })
                              }
                              className="rounded-lg border border-teal-300/40 bg-teal-500/10 px-3 py-2 text-sm font-semibold text-teal-100 transition hover:bg-teal-500/20"
                            >
                              Appeal Proof
                            </button>
                            <span
                              className={`inline-flex rounded-full border px-2 py-1 text-xs font-semibold ${
                                p.appeal_proof_integrity?.status === "verified"
                                  ? "border-teal-300/40 bg-teal-500/15 text-teal-200"
                                  : p.appeal_proof_integrity?.status === "tampered"
                                  ? "border-red-300/40 bg-red-500/15 text-red-200"
                                  : "border-amber-300/40 bg-amber-500/15 text-amber-200"
                              }`}
                            >
                              Appeal {p.appeal_proof_integrity?.status || "unknown"}
                            </span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      {p.status === "pending" ? (
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleStatusChange(p.id, "approve")}
                            className="rounded-lg bg-teal-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-teal-500"
                          >
                            Approve
                          </button>
                          <ActionMenu context={`payment ${p.id}`}><button
                            onClick={() => openReviewModal(p, "clarify")}
                            className="rounded-lg border border-amber-300/30 bg-amber-500/15 px-3 py-2 text-sm font-semibold text-amber-100 transition hover:bg-amber-500/25"
                          >
                            Clarify
                          </button>
                          <button
                            onClick={() => openReviewModal(p, "reject")}
                            className="rounded-lg border border-red-300/30 bg-red-500/15 px-3 py-2 text-sm font-semibold text-red-100 transition hover:bg-red-500/25"
                          >
                            Reject
                          </button></ActionMenu>
                        </div>
                      ) : (
                        <div className="flex justify-end gap-2">
                          <div className="text-right text-sm text-slate-400">Completed</div>
                          {p.status === "approved" && (
                            <button
                              onClick={() => receiptForPayment(p.id)}
                              className="rounded-lg border border-white/15 px-3 py-2 text-sm font-semibold text-slate-100 transition hover:bg-white/10"
                            >
                              Receipt
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="11" className="px-5 py-10 text-center text-slate-300">
                    No payments found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        </div>

        <div className="payment-review-cards flex flex-col gap-4 md:hidden">
          {filteredPayments.length > 0 ? (
            filteredPayments.map((p) => (
              <div
                key={p.id}
                className="rounded-lg border border-white/10 bg-slate-950/90 p-4 shadow-xl"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-bold">
                      {p.tenant_name || "Unknown Tenant"}
                    </h3>
                    <p className="mt-1 font-semibold text-teal-200">
                      {formatAmount(p.amount)}
                    </p>
                    <p className="mt-1 text-sm text-slate-300">
                      {p.property?.property_name || "Linked rental"}
                    </p>
                    <p className="mt-1 text-sm text-slate-300">
                      {p.method} - {p.month_paid_for || "-"}
                    </p>
                  </div>

                  <span
                    className={`shrink-0 rounded-full border px-3 py-1 text-xs font-semibold capitalize ${statusClass(
                      p.status
                    )}`}
                  >
                    {statusLabel(p.status)}
                  </span>
                </div>

                <div className="mt-3 rounded-lg border border-white/10 bg-white/5 p-3 text-xs text-slate-400">
                  <p>Submitted: {formatDateTime(p.created_at)}</p>
                  {p.approved_at && <p>Approved: {formatDateTime(p.approved_at)}</p>}
                  {p.rejected_at && <p>Rejected: {formatDateTime(p.rejected_at)}</p>}
                  {p.clarification_at && <p>Clarified: {formatDateTime(p.clarification_at)}</p>}
                  {p.appealed_at && <p>Appealed: {formatDateTime(p.appealed_at)}</p>}
                  <p className="mt-1">
                    Aging:{" "}
                    <span className="font-semibold text-white">{paymentAging(p).label}</span>
                  </p>
                </div>

                {(p.rejected_reason || p.clarification_reason || p.appeal_message) && (
                  <p
                    className={`mt-3 rounded-lg p-3 text-sm ${
                      p.rejected_reason
                        ? "bg-red-500/10 text-red-100"
                        : "bg-amber-500/10 text-amber-100"
                    }`}
                  >
                    {p.appeal_message || p.clarification_reason || p.rejected_reason}
                  </p>
                )}

                <div className="mt-4 grid grid-cols-2 gap-3 rounded-lg border border-white/10 bg-white/5 p-3 text-sm">
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-500">
                      Type
                    </p>
                    <p className="mt-1 font-semibold capitalize text-white">
                      {p.payment_type || "partial"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-500">
                      Balance
                    </p>
                    <p className="mt-1 font-semibold text-teal-200">
                      {formatAmount(p.balance_after)}
                    </p>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {p.proof_path && (
                    <button
                      onClick={() =>
                        setProofModal({ data: p.proof_path, title: "Payment Proof" })
                      }
                      className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm font-semibold text-slate-100"
                    >
                      View Proof
                    </button>
                  )}
                  {p.proof_path && (
                    <span
                      className={`inline-flex rounded-full border px-2 py-1 text-xs font-semibold ${
                        p.proof_integrity?.status === "verified"
                          ? "border-teal-300/40 bg-teal-500/15 text-teal-200"
                          : p.proof_integrity?.status === "tampered"
                          ? "border-red-300/40 bg-red-500/15 text-red-200"
                          : "border-amber-300/40 bg-amber-500/15 text-amber-200"
                      }`}
                    >
                      Proof {p.proof_integrity?.status || "unknown"}
                    </span>
                  )}
                  {p.appeal_proof_path && (
                    <button
                      onClick={() =>
                        setProofModal({ data: p.appeal_proof_path, title: "Appeal Proof" })
                      }
                      className="rounded-lg border border-teal-300/40 bg-teal-500/10 px-3 py-2 text-sm font-semibold text-teal-100"
                    >
                      Appeal Proof
                    </button>
                  )}
                  {p.appeal_proof_path && (
                    <span
                      className={`inline-flex rounded-full border px-2 py-1 text-xs font-semibold ${
                        p.appeal_proof_integrity?.status === "verified"
                          ? "border-teal-300/40 bg-teal-500/15 text-teal-200"
                          : p.appeal_proof_integrity?.status === "tampered"
                          ? "border-red-300/40 bg-red-500/15 text-red-200"
                          : "border-amber-300/40 bg-amber-500/15 text-amber-200"
                      }`}
                    >
                      Appeal {p.appeal_proof_integrity?.status || "unknown"}
                    </span>
                  )}

                  {p.status === "pending" && (
                    <>
                      <button
                        onClick={() => handleStatusChange(p.id, "approve")}
                        className="flex-1 rounded-lg bg-teal-600 px-3 py-2 text-sm font-semibold text-white"
                      >
                        Approve
                      </button>
                      <ActionMenu context={`payment ${p.id}`}><button
                        onClick={() => openReviewModal(p, "clarify")}
                        className="flex-1 rounded-lg border border-amber-300/30 bg-amber-500/15 px-3 py-2 text-sm font-semibold text-amber-100"
                      >
                        Clarify
                      </button>
                      <button
                        onClick={() => openReviewModal(p, "reject")}
                        className="flex-1 rounded-lg border border-red-300/30 bg-red-500/15 px-3 py-2 text-sm font-semibold text-red-100"
                      >
                        Reject
                      </button></ActionMenu>
                    </>
                  )}
                  {p.status === "approved" && (
                    <button
                      onClick={() => receiptForPayment(p.id)}
                      className="rounded-lg border border-white/15 px-3 py-2 text-sm font-semibold text-slate-100"
                    >
                      Receipt
                    </button>
                  )}
                </div>
              </div>
            ))
          ) : (
            <p className="rounded-lg border border-dashed border-white/15 py-8 text-center text-slate-300">
              No payments found.
            </p>
          )}
        </div>
        </>}
      </div>
    </div>
  );
}
